import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useState, useEffect } from "react";

const rotatingWords = [
  "Merge",
  "Split",
  "Convert",
  "Encrypt",
  "Repair",
  "Edit",
  "Sign",
  "Redact",
];

const funPuns = [
  "I also love PDFs, they're so private.",
  "Privacy matters",
  "No ads, no tracking, no data collection.",
  "No PDFs were harmed in the making of this website.",
  "What happens in your browser, stays in your browser.",
  "We're so private, even we don't know what you're doing",
  "Your PDFs called. They want privacy too.",
  "Cloud-free since 2026.",
  "No third-party cookies, no analytics.",
  "Our servers are on vacation. Permanently.",
];

export const HeroSection = () => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentPunIndex, setCurrentPunIndex] = useState(0);

  useEffect(() => {
    const wordInterval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentWordIndex((prev) => (prev + 1) % rotatingWords.length);
        setIsAnimating(false);
      }, 200);
    }, 2500);

    return () => clearInterval(wordInterval);
  }, []);

  useEffect(() => {
    const punInterval = setInterval(() => {
      setCurrentPunIndex((prev) => (prev + 1) % funPuns.length);
    }, 5000);

    return () => clearInterval(punInterval);
  }, []);

  return (
    <section className="relative bg-transparent">
      <div className="relative mx-auto max-w-4xl px-4 py-20 md:py-32 text-center">
        {/* Main Content */}
        <div className="space-y-8">
          {/* Main Heading with Rotating Words */}
          <div className="space-y-4">
            <div className="flex justify-center">
              <a
                href="https://x.com/rahil1202/status/2029173589602345347?s=20"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
                style={{
                  background: "rgba(0, 0, 0, 0.8)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  border: "1px solid rgba(0, 0, 0, 0.15)",
                  animation: "glass-glow 3s ease-in-out infinite",
                }}
              >
                {/* Shimmer sweep */}
                <span
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, rgba(0,0,0,0.12), transparent)",
                    animation: "glass-shimmer 3s ease-in-out infinite",
                  }}
                />
                <span className="relative">🎉</span>
                <span className="relative text-white">
                  5,000+ users and counting! V2 launching soon!
                </span>
                <svg
                  className="relative h-3.5 w-3.5 text-white transition-transform duration-300 group-hover:translate-x-0.5"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
            </div>

            <h1 className="font-serifAccent text-4xl leading-tight tracking-tight text-foreground md:text-6xl lg:text-7xl">
              Your free online PDF workspace
            </h1>

            <p className="text-sm font-medium uppercase tracking-[0.28em] text-foreground/55">
              PDFWorkSpace
            </p>

            <p className="font-serifAccent text-2xl leading-tight tracking-tight text-foreground/80 md:text-4xl">
              <span className="sr-only">
                Private, client-side PDF repair tools
              </span>
              <span
                className={`inline-block min-w-[180px] md:min-w-[240px] transition-all duration-200 ${
                  isAnimating
                    ? "opacity-0 -translate-y-3"
                    : "opacity-100 translate-y-0"
                }`}
              >
                {rotatingWords[currentWordIndex]}
              </span>{" "}
              PDFs privately.
            </p>

            <p className="mx-auto max-w-xl text-lg text-text-secondary leading-relaxed md:text-xl">
              PDFWorkSpace gives you a private PDF space to merge, split,
              edit, OCR, quiz, and sign documents right in your browser. No
              uploads, no accounts, and no waiting for files to come back from
              a server.
            </p>
          </div>

          {/* CTA Button */}
          <div className="pt-4">
            <Link
              to="/tools"
              className="group inline-flex items-center gap-2 rounded-full bg-foreground px-8 py-4 text-base font-medium text-background transition-all hover:bg-foreground/90 hover:gap-3 hover:shadow-xl hover:shadow-foreground/10"
            >
              Start using tools — it's free, it's private, it's fast.
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

          {/* Fun Pun - Rotating */}
          <div className="pt-2">
            <p className="text-base text-text-secondary/60 italic transition-all duration-500">
              "{funPuns[currentPunIndex]}"
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
